import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/constants";

const CAPS = [
    {
        n: "I.",
        title: "Carbon Exposure Detection Engine",
        eyebrow: "Identify",
        body: "Granular emissions intelligence across MSME operations and lender portfolios. Sector-calibrated activity models compute Scope 1, 2, and prioritized Scope 3.",
        img: IMAGES.satellite,
        tags: ["Scope 1–3", "Activity-based", "Portfolio Roll-up"]
    },
    {
        n: "II.",
        title: "The Carbonis Score",
        eyebrow: "Quantify",
        body: "A single, defensible risk score that translates raw emissions into regulatory, credit, and margin risk. Explainable, confidence-weighted, and auditable — built for credit committees and supervisory review.",
        img: IMAGES.boardroom,
        tags: ["Explainable", "Confidence-Weighted", "Bank-Grade", "Regulator-Ready"]
    },
    {
        n: "III.",
        title: "Mitigation Pathways",
        eyebrow: "Mitigate",
        body: "Operational efficiency actions ranked by abatement cost, paired with vetted carbon instruments to neutralize residual exposure. Actionable, not aspirational.",
        img: IMAGES.factory,
        tags: ["MACC", "Removals", "Compliance Offsets"]
    },
    {
        n: "IV.",
        title: "Decision Dashboards",
        eyebrow: "Protect",
        body: "Terminal-grade dashboards purpose-built for CFOs, CROs, and credit committees. Risk-adjusted decisions, ready for audit and board packs.",
        img: IMAGES.dashboard,
        tags: ["CFO View", "Risk Committee", "Auditable"]
    }
];

export default function Platform({ compact = false }) {
    return (
        <section id="platform" className="relative py-24 lg:py-36 border-t border-navy/10" data-testid="platform-section">
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">The Platform</span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em]">
                            Four instruments. One <em className="text-gold not-italic font-light">operating layer</em> for carbon risk.
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 lg:self-end">
                        <p className="text-navy/60 text-lg leading-relaxed">
                            Carbonis converts carbon data into actionable risk intelligence — from the shop floor of an MSME to the credit committee of a scheduled commercial bank.
                        </p>
                    </div>
                </div>

                <div className="flex flex-col gap-16 lg:gap-24">
                    {CAPS.map((c, i) => (
                        <motion.article
                            key={c.n}
                            initial={{ opacity: 0, y: 60 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
                                i % 2 === 1 ? "lg:[direction:rtl]" : ""
                            }`}
                            data-testid={`platform-cap-${i}`}
                        >
                            <div className="lg:col-span-6 lg:[direction:ltr]">
                                <div className="relative aspect-[4/3] overflow-hidden group">
                                    <motion.img
                                        src={c.img}
                                        alt={c.title}
                                        className="w-full h-full object-cover"
                                        style={{ filter: "grayscale(45%) contrast(1.05) brightness(0.75)" }}
                                        whileHover={{ scale: 1.04 }}
                                        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-tr from-paper/70 via-transparent to-transparent"></div>
                                    <div className="absolute top-6 left-6 font-serif text-5xl text-gold">{c.n}</div>
                                    <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                                        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/70">{c.eyebrow}</span>
                                        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50">Fig. 0{i + 1}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="lg:col-span-5 lg:col-start-8 lg:[direction:ltr]">
                                <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">{c.eyebrow}</span>
                                <h3 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.02em] text-navy">
                                    {c.title}
                                </h3>
                                <p className="mt-6 text-navy/60 text-base lg:text-lg leading-relaxed max-w-[520px]">{c.body}</p>
                                <div className="mt-8 flex flex-wrap gap-2">
                                    {c.tags.map((t) => (
                                        <span
                                            key={t}
                                            className="text-[10px] font-mono uppercase tracking-[0.22em] text-navy/70 border border-navy/15 px-3 py-2"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
}
